import type { FixedAssetResources } from './en-US.js';

export const zhHans: FixedAssetResources = {
  nav: {
    myAssets: '我的资产',
    assets: '资产列表',
    locations: '存放地点',
    inventories: '盘点',
    import: '导入',
  },
  common: {
    none: '—',
    unknown: '未知',
    all: '全部',
    save: '保存',
    saved: '已保存。',
    reload: '重新加载',
    edit: '编辑',
    add: '添加',
    active: '使用中',
    inactive: '已停用',
    deactivate: '停用',
    activate: '重新启用',
  },
  field: {
    code: '资产编号',
    name: '名称',
    type: '资产类型',
    accountTitle: '会计科目',
    assetClass: '资产分类',
    tangibility: '有形/无形',
    site: '据点',
    location: '存放地点',
    user: '使用人',
    manager: '资产管理员',
    status: '状态',
    acquiredOn: '购置日期',
    currency: '货币',
    amount: '购置金额',
    amountJpy: '日元金额',
    usefulLifeYears: '使用年限',
    maker: '制造商',
    model: '型号',
    serialNumber: '序列号',
    previousCode: '原资产编号',
    notes: '备注',
    disposedOn: '处置日期',
    disposalMethod: '处置方式',
    disposalNote: '处置说明',
  },
  myAssets: {
    title: '我的资产',
    empty: '没有分配给您的资产。',
    inventoryBanner: '盘点“{{title}}”正在进行。请确认您手头上的资产。',
    confirm: '确认',
    confirmed: '已确认',
  },
  assets: {
    title: '资产列表',
    register: '登记资产',
    export: '导出 CSV',
    search: '搜索',
    searchPlaceholder: '资产编号、名称、制造商、型号、序列号',
    allSites: '所有据点',
    allTypes: '所有资产类型',
    openStatuses: '已处置以外',
    jpyMissing: '缺少日元金额',
    userDisabled: '使用人已停用',
    empty: '没有符合条件的资产。',
    printLabels: '打印标签（{{selected}} 项）',
    clearSelection: '取消选择',
    selectPage: '全选本页',
    selectionLimit: '最多可选择 {{max}} 项资产打印标签。',
  },
  assetForm: {
    registerTitle: '登记资产',
    editTitle: '编辑资产',
    register: '登记',
    registerAnother: '按相同内容继续登记',
    registered: '已登记 {{code}}。',
    jpyHint: '日本据点使用日元以外的货币时必填。',
    siteNotConfigured: '尚未设置（据点的国家和货币由管理员设置）。',
    noUser: '无使用人',
    chooseUser: '选择使用人',
  },
  asset: {
    details: '详情',
    history: '历史记录',
    actions: '操作',
    changeUser: '变更使用人',
    changeManager: '变更资产管理员',
    changeLocation: '变更存放地点',
    changeSite: '移至其他据点',
    changeStatus: '变更状态',
    dispose: '处置',
    revertDisposal: '撤销处置',
    confirmRevert: '要撤销 {{code}} 的处置吗？资产将恢复到处置前的状态。',
    conflict: '此资产已在其他地方被修改，请重新加载以查看最新状态。',
    jpyMissing: '缺少日元金额（日本据点必填）。',
    userDisabled: '使用人的账户已在 mai ID 中停用，请指定其他使用人。',
    noUser: '无使用人',
    noLocation: '无存放地点',
    hidden: '不显示',
    disposalNoteHint: '选择“其他”时必填。',
    attachments: '附件',
    printLabel: '打印标签',
    confirmIn: '在“{{title}}”中确认',
    confirmedIn: '已在“{{title}}”中确认',
  },
  history: {
    empty: '暂无历史记录。',
    periods: '使用期间',
    period: '{{from}} – {{to}}',
    current: '至今',
    by: '{{name}}',
    system: '系统',
  },
  card: {
    title: '资产 {{code}}',
    returnHint: '如果您发现了此资产，请归还给资产管理员。',
    open: '打开资产',
  },
  attachments: {
    photos: '照片',
    documents: '文件',
    addPhoto: '添加照片',
    addDocument: '添加文件',
    hint: 'JPEG、PNG、WebP 或 PDF，最大 {{maxMiB}} MB。照片会在上传前缩小。',
    full: '附件已达上限 {{max}} 个。',
    uploading: '正在上传…',
    empty: '暂无附件。',
    remove: '删除附件',
    removeNamed: '删除 {{name}}',
    confirmRemove: '确定删除 {{name}} 吗？删除后将不再显示，但会作为记录保留。',
  },
  labels: {
    title: '打印二维码标签',
    print: '打印',
    layout: '标签纸',
    layoutA4: 'A4 标签纸，24 张（70 × 37 mm）',
    layoutSingle: '标签打印机（62 × 29 mm）',
    start: '起始位置',
    startHint: '跳过标签纸上已使用的标签（1 = 左上）。',
    summary: '共 {{labels}} 张标签，{{sheets}} 页。',
    printHint: '在打印对话框中请选择 100% 缩放、无边距。',
    missing: '未找到的资产编号：{{codes}}',
    noneSelected: '未选择资产。请在资产列表中选择。',
    toAssets: '前往资产列表',
  },
  inventories: {
    title: '盘点',
    start: '开始盘点',
    titleField: '标题',
    scope: '盘点的存放地点',
    wholeSite: '整个据点',
    startHint: '盘点对象为开始时该据点（或所选存放地点）的资产，之后登记的资产不包含在内。',
    empty: '尚无盘点。',
    startedAt: '开始时间',
    closedAt: '完成时间',
    progress: '已盘点 {{checked}} / {{total}}',
  },
  inventory: {
    close: '完成盘点',
    confirmClose: '确定完成“{{title}}”吗？未盘点的资产将保持未盘点，资产信息不会被更改。',
    export: '导出结果（CSV）',
    expectedLocation: '登记存放地点',
    expectedUser: '登记使用人',
    result: '盘点结果',
    foundLocation: '实际存放地点',
    locationDiffers: '存放地点不符',
    onlyDiffers: '仅存放地点不符',
    selfConfirmed: '使用人自行确认',
    checked: '盘点人',
    note: '备注',
    check: '盘点',
    checkTitle: '盘点 {{code}}',
    found: '已确认',
    search: '资产编号或名称',
    closedNotice:
      '此盘点已完成。如需将未找到的资产设为遗失，或移至实际存放地点，请在各资产页面操作。',
  },
  import: {
    title: '从 CSV 导入',
    intro:
      '请在电子表格中填写模板并保存为 CSV。资产类型以前缀指定，据点以名称指定，使用人和资产管理员以员工编号指定。表头和选项可使用四种语言中的任一种。',
    template: '下载模板',
    chooseFile: '选择 CSV 文件',
    encoding: '字符编码',
    encodings: {
      utf8: 'UTF-8',
      shiftJis: 'Shift_JIS（日文版 Excel）',
      gb18030: 'GB18030（简体中文版 Excel）',
      big5: 'Big5（繁体中文版 Excel）',
    },
    createLocations: '创建尚不存在的存放地点',
    preview: '检查文件',
    valid: '未发现问题。将登记 {{rows}} 项资产。',
    invalid: '发现 {{issues}} 个问题。请修改文件后重新检查；存在问题时不会导入任何内容。',
    missingLocations: '尚不存在的存放地点：{{names}}',
    commit: '导入 {{rows}} 项资产',
    done: '已登记 {{rows}} 项资产。',
    locationsCreated: '已创建 {{created}} 个存放地点。',
    downloadCodes: '下载新资产编号（CSV）',
    row: '行',
    column: '列',
    problem: '问题',
    columns: {
      userEmployeeNumber: '使用人员工编号',
      managerEmployeeNumber: '资产管理员员工编号',
    },
    problems: {
      Required: '必填',
      Invalid: '值无效',
      TooLong: '过长',
      UnknownAssetType: '没有此前缀或名称的资产类型',
      InactiveAssetType: '此资产类型已停用',
      UnknownSite: '没有此名称的据点',
      SiteNotConfigured: '据点的国家和货币尚未设置',
      UnknownLocation: '该据点没有此存放地点',
      InactiveLocation: '此存放地点已停用',
      UnknownEmployee: '没有此员工编号的用户',
      UserDisabled: '该用户的账号已停用',
      NotAssetManager: '不是资产管理员',
      StatusNotAllowed: '导入时状态只能为使用中、闲置或维修中',
      FutureDate: '日期在未来',
      InvalidCurrency: '货币代码无效',
      InvalidAmount: '金额与货币不符',
      JpyAmountRequired: '日本据点必须填写日元金额',
      AssetNumberExhausted: '此资产类型的资产编号已用完',
      UnknownColumn: '未知的列',
      DuplicateColumn: '列重复',
      MissingColumn: '缺少必填列',
    },
  },
  locations: {
    title: '存放地点',
    add: '添加存放地点',
    empty: '该据点尚无存放地点。',
  },
  assetTypes: {
    title: '资产类型',
    description:
      '每种资产类型都有一个 1–4 个字母的前缀，作为资产编号的开头（例如 PC0000000001）。发放资产编号后，前缀无法更改。',
    add: '添加资产类型',
    prefix: '前缀',
    prefixHint: '1–4 个字母 A–Z',
    prefixLocked: '已发放资产编号，前缀无法更改。',
    defaultAssetClass: '默认资产分类',
    defaultTangibility: '默认有形/无形',
    defaultUsefulLife: '默认使用年限',
    empty: '尚无资产类型。',
  },
  sites: {
    title: '据点',
    description:
      'mai ID 的每个群组都是一个据点。设置国家和默认货币后，即可在该据点登记资产。日本据点还必须填写日元金额。',
    country: '国家',
    defaultCurrency: '默认货币',
    notConfigured: '未设置',
    jpyRequired: '需要日元金额',
    set: '设置',
  },
  userPicker: {
    search: '姓名、邮箱或员工编号',
  },
  assetManagers: {
    title: '资产管理员',
    description:
      '资产管理员可以登记和修改所有据点的资产与存放地点，并指定使用人。管理员始终可以执行这些操作。',
    add: '添加资产管理员',
    name: '姓名',
    employeeNumber: '员工编号',
    site: '据点',
    grantedAt: '添加时间',
    managedAssets: '管理中的资产',
    remove: '移除',
    confirmRemove: '要将 {{name}} 从资产管理员中移除吗？',
    empty: '尚未添加资产管理员。',
    unknownUser: '未知用户',
    handOver: '移交资产',
    handOverTo: '接收的资产管理员',
    handedOver: '已移交 {{count}} 项资产。',
  },
  audit: {
    title: '审计日志',
    time: '时间',
    actor: '操作人',
    action: '操作',
    target: '对象',
    system: '系统',
    actions: {
      AssetTypeChanged: '变更资产类型',
      SiteSettingsChanged: '变更据点设置',
      StorageLocationChanged: '变更存放地点',
      AssetManagerGranted: '添加资产管理员',
      AssetManagerRevoked: '移除资产管理员',
      AssetDisposalReverted: '撤销处置',
      AssetsImported: '导入资产',
      AssetCsvExported: '导出资产 CSV',
      InventoryStarted: '开始盘点',
      InventoryClosed: '完成盘点',
      LocalAdminGranted: '添加管理员',
      LocalAdminRevoked: '移除管理员',
    },
  },
};
